import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import Table from "../../extra/Table";
import Iconb from "../../extra/Iconb";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  getSupportConversations,
  updateSupportStatus,
  updateSupportPriority,
  getSupportStats,
  createSupportConversation,
} from "../../store/supportChat/supportChat.action";
import { apiInstanceFetch } from "../../../util/api";
import dayjs from "dayjs";
import Pagination from "../../extra/Pagination";
import Searching from "../../extra/Searching";
import Skeleton from "react-loading-skeleton";
import { colors } from "../../../util/SkeletonColor";
import "react-loading-skeleton/dist/skeleton.css";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import SearchIcon from "@mui/icons-material/Search";

const SupportChat = (props) => {
  const [size, setSize] = useState(10);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  // New Chat dialog state
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newChatSubject, setNewChatSubject] = useState("");

  const { conversations, totalConversations, stats } = useSelector(
    (state) => state.supportChat
  );
  const { admin } = useSelector((state) => state.admin);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getSupportConversations(currentPage, size, statusFilter, priorityFilter));
    dispatch(getSupportStats());
  }, [dispatch, currentPage, size, statusFilter, priorityFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setData(conversations);
  }, [conversations]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event), 10);
    setSize(parseInt(event), 10);
    setCurrentPage(1);
  };

  const handleOpen = (id) => {
    navigate("/admin/supportChatDetails", { state: id });
  };

  const handleFilterData = (filteredData) => {
    if (typeof filteredData === "string") {
      setSearch(filteredData);
    } else {
      setData(filteredData);
    }
  };

  // New Chat dialog handlers
  const handleSearchUsers = useCallback((value) => {
    setUserSearch(value);
    if (value.trim().length < 2) {
      setUserResults([]);
      return;
    }
    setUserSearchLoading(true);
    apiInstanceFetch
      .get(`user?start=1&limit=10&search=${encodeURIComponent(value.trim())}`)
      .then((res) => {
        setUserResults(res.users || []);
      })
      .catch(() => setUserResults([]))
      .finally(() => setUserSearchLoading(false));
  }, []);

  const handleOpenNewChat = () => {
    setNewChatOpen(true);
    setUserSearch("");
    setUserResults([]);
    setSelectedUser(null);
    setNewChatSubject("");
  };

  const handleCloseNewChat = () => {
    setNewChatOpen(false);
  };

  const handleStartChat = () => {
    if (!selectedUser) return;
    dispatch(
      createSupportConversation(
        selectedUser._id,
        newChatSubject || "General Inquiry",
        admin._id,
        navigate
      )
    );
    setNewChatOpen(false);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      open: "bg-primary",
      pending: "bg-warning",
      resolved: "bg-success",
      closed: "bg-secondary",
    };
    return (
      <span
        className={`badge ${statusColors[status] || "bg-secondary"}`}
        style={{ padding: "8px 12px", fontSize: "12px", textTransform: "capitalize" }}
      >
        {status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      low: "bg-info",
      medium: "bg-primary",
      high: "bg-warning",
      urgent: "bg-danger",
    };
    return (
      <span
        className={`badge ${priorityColors[priority] || "bg-secondary"}`}
        style={{ padding: "8px 12px", fontSize: "12px", textTransform: "capitalize" }}
      >
        {priority === "urgent" && <PriorityHighIcon sx={{ fontSize: 14, mr: 0.5 }} />}
        {priority}
      </span>
    );
  };

  const mapData = [
    {
      Header: "No",
      width: "20px",
      Cell: ({ index }) => (
        <span className="text-white">
          {(currentPage - 1) * rowsPerPage + index + 1}
        </span>
      ),
    },
    {
      Header: "User",
      body: "user",
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div className="position-relative">
            {loading ? (
              <Skeleton
                height={42}
                width={42}
                style={{ borderRadius: "90px" }}
                baseColor={colors?.baseColor}
                highlightColor={colors?.highlightColor}
              />
            ) : (
              <img
                src={row?.user?.image}
                height={45}
                width={45}
                alt=""
                style={{ borderRadius: "90px", objectFit: "cover" }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/dummy.png";
                }}
              />
            )}
          </div>
          <span className="fw-normal ms-3 text-start">
            <b className="fw-normal text-capitalize text-white">
              {row.user?.fullName || `${row.user?.firstName || ""} ${row.user?.lastName || ""}`}
            </b>
            <br />
            <small className="text-secondary">{row.user?.email}</small>
          </span>
        </div>
      ),
      sorting: false,
    },
    {
      Header: "Subject",
      body: "subject",
      Cell: ({ row }) => (
        <span className="text-white" style={{ maxWidth: "150px" }}>
          {row.subject || "General Inquiry"}
        </span>
      ),
    },
    {
      Header: "Last Message",
      body: "lastMessage",
      Cell: ({ row }) => (
        <div className="text-white" style={{ maxWidth: "200px" }}>
          <span className="text-truncate d-block">
            {row.lastMessage?.content || "No messages yet"}
          </span>
          <small className="text-secondary">
            {row.lastMessage?.senderType === "User" ? "User" : "Support"}
          </small>
        </div>
      ),
      sorting: false,
    },
    {
      Header: "Unread",
      body: "unread",
      Cell: ({ row }) => {
        const unread = row.supportUnreadCount || 0;
        return (
          <span
            className={`badge ${unread > 0 ? "bg-danger" : "bg-secondary"}`}
            style={{ padding: "8px 12px", fontSize: "12px" }}
          >
            {unread}
          </span>
        );
      },
    },
    {
      Header: "Priority",
      body: "priority",
      Cell: ({ row }) => getPriorityBadge(row.priority),
    },
    {
      Header: "Status",
      body: "status",
      Cell: ({ row }) => getStatusBadge(row.status),
    },
    {
      Header: "Assigned To",
      body: "assignedAgent",
      Cell: ({ row }) => (
        <span className="text-white">
          {row.assignedAgent?.name || (
            <span className="text-secondary">Unassigned</span>
          )}
        </span>
      ),
    },
    {
      Header: "Last Activity",
      body: "lastMessageAt",
      Cell: ({ row }) => (
        <span className="text-white">
          {row.lastMessageAt
            ? dayjs(row.lastMessageAt).format("DD MMM YYYY HH:mm")
            : "-"}
        </span>
      ),
    },
    {
      Header: "View",
      body: "",
      Cell: ({ row }) => (
        <Iconb
          type="button"
          newClass="boxCenter infobtn userBtn"
          btnIcon={<ChatBubbleOutlineIcon sx={{ color: "#737272" }} />}
          style={{
            borderRadius: "80px",
            margin: "auto",
            height: "38px",
            width: "38px",
          }}
          onClick={() => handleOpen(row._id)}
        />
      ),
    },
  ];

  return (
    <>
      <div className="mainUserTable">
        <div className="userTable">
          <div className="col-12 headname d-flex align-items-center">
            <SupportAgentIcon sx={{ mr: 1 }} /> Support Chat
            <button
              className="ms-3 d-flex align-items-center gap-1"
              style={{
                background: "#F0C420", color: "#333", border: "none",
                borderRadius: "20px", fontSize: "13px", fontWeight: 600,
                padding: "6px 16px", cursor: "pointer",
              }}
              onClick={handleOpenNewChat}
            >
              <AddCommentOutlinedIcon sx={{ fontSize: 16 }} />
              New Chat
            </button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="row mb-3 px-3">
              <div className="col-md-2">
                <div className="card bg-primary text-white p-3 text-center">
                  <h4>{stats.totalOpen || 0}</h4>
                  <small>Open</small>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-warning text-dark p-3 text-center">
                  <h4>{stats.totalPending || 0}</h4>
                  <small>Pending</small>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-success text-white p-3 text-center">
                  <h4>{stats.totalResolved || 0}</h4>
                  <small>Resolved</small>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-danger text-white p-3 text-center">
                  <h4>{stats.totalUrgent || 0}</h4>
                  <small>Urgent</small>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-info text-white p-3 text-center">
                  <h4>{stats.totalUnread || 0}</h4>
                  <small>Unread</small>
                </div>
              </div>
            </div>
          )}

          <div className="userMain">
            <div className="tableMain mt-2">
              <div className="userHeader primeHeader">
                <div className="row">
                  <div className="col-4">
                    <div className="d-flex gap-2">
                      <select
                        className="form-select form-select-sm"
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        style={{ maxWidth: "150px" }}
                      >
                        <option value="all">All Status</option>
                        <option value="open">Open</option>
                        <option value="pending">Pending</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                      <select
                        className="form-select form-select-sm"
                        value={priorityFilter}
                        onChange={(e) => {
                          setPriorityFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                        style={{ maxWidth: "150px" }}
                      >
                        <option value="all">All Priority</option>
                        <option value="urgent">Urgent</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-8">
                    <Searching
                      type="client"
                      data={conversations}
                      setData={setData}
                      column={data}
                      onFilterData={handleFilterData}
                      serverSearching={handleFilterData}
                      button={true}
                      setSearchValue={setSearch}
                      searchValue={search}
                    />
                  </div>
                </div>
              </div>
              <Table
                data={data}
                mapData={mapData}
                serverPerPage={rowsPerPage}
                serverPage={currentPage}
                type="server"
              />
              <Pagination
                component="div"
                count={conversations?.length}
                type="server"
                onPageChange={handleChangePage}
                serverPerPage={rowsPerPage}
                totalData={totalConversations}
                serverPage={currentPage}
                setCurrentPage={setCurrentPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </div>
          <div className="userFooter primeFooter"></div>
        </div>
      </div>

      {/* New Chat Dialog - rendered via portal to escape parent CSS */}
      {newChatOpen && ReactDOM.createPortal(
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.35)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={handleCloseNewChat}
        >
          <div
            style={{
              backgroundColor: "#ffffff", borderRadius: "16px", width: "460px",
              maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column",
              boxShadow: "0 8px 30px rgba(0,0,0,0.15)", color: "#333",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Dialog Header */}
            <div style={{ padding: "20px 24px 12px", borderBottom: "1px solid #eee" }}>
              <h5 style={{ color: "#333", margin: "0 0 14px", fontWeight: 600 }}>Start New Chat</h5>

              {/* User Search */}
              <div className="input-group mb-2">
                <span className="input-group-text" style={{ background: "#f5f5f5", border: "1px solid #ddd" }}>
                  <SearchIcon sx={{ color: "#999", fontSize: 20 }} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search user by name or email..."
                  value={userSearch}
                  onChange={(e) => handleSearchUsers(e.target.value)}
                  style={{ background: "#f5f5f5", border: "1px solid #ddd", color: "#333" }}
                  autoFocus
                />
              </div>
            </div>

            {/* User Results */}
            <div style={{ flex: 1, overflowY: "auto", padding: "8px 24px", maxHeight: "300px" }}>
              {userSearchLoading && (
                <div className="text-center py-3" style={{ color: "#999" }}>Searching...</div>
              )}
              {!userSearchLoading && userSearch.length >= 2 && userResults.length === 0 && (
                <div className="text-center py-3" style={{ color: "#999" }}>No users found</div>
              )}
              {!userSearchLoading && userSearch.length < 2 && (
                <div className="text-center py-3" style={{ color: "#999" }}>Type at least 2 characters to search</div>
              )}
              {userResults.map((u) => (
                <div
                  key={u._id}
                  className="d-flex align-items-center p-2 mb-1"
                  style={{
                    cursor: "pointer", borderRadius: "10px",
                    background: selectedUser?._id === u._id ? "#F0C42020" : "transparent",
                    border: selectedUser?._id === u._id ? "2px solid #F0C420" : "2px solid transparent",
                  }}
                  onClick={() => setSelectedUser(u)}
                >
                  <img
                    src={u.image || "/dummy.png"}
                    alt=""
                    style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover" }}
                    onError={(e) => { e.target.onerror = null; e.target.src = "/dummy.png"; }}
                  />
                  <div className="ms-3">
                    <div className="text-capitalize" style={{ fontSize: "14px", fontWeight: 500, color: "#333" }}>
                      {u.firstName} {u.lastName}
                    </div>
                    <small style={{ color: "#888" }}>{u.email}</small>
                  </div>
                </div>
              ))}
            </div>

            {/* Subject + Actions */}
            <div style={{ padding: "12px 24px 20px", borderTop: "1px solid #eee" }}>
              {selectedUser && (
                <div className="mb-2">
                  <small style={{ color: "#888" }}>Selected: </small>
                  <span className="text-capitalize fw-bold" style={{ color: "#333" }}>
                    {selectedUser.firstName} {selectedUser.lastName}
                  </span>
                </div>
              )}
              <input
                type="text"
                className="form-control mb-3"
                placeholder="Subject (optional)"
                value={newChatSubject}
                onChange={(e) => setNewChatSubject(e.target.value)}
                style={{ background: "#f5f5f5", border: "1px solid #ddd", color: "#333", borderRadius: "10px" }}
              />
              <div className="d-flex justify-content-end gap-2">
                <button
                  className="btn btn-sm"
                  style={{ color: "#666", border: "1px solid #ddd", borderRadius: "20px", padding: "6px 18px" }}
                  onClick={handleCloseNewChat}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-sm"
                  style={{
                    background: "#F0C420", color: "#333", fontWeight: 600,
                    borderRadius: "20px", padding: "6px 18px", border: "none",
                    opacity: selectedUser ? 1 : 0.5,
                  }}
                  onClick={handleStartChat}
                  disabled={!selectedUser}
                >
                  Start Chat
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default connect(null, {
  getSupportConversations,
  updateSupportStatus,
  updateSupportPriority,
  getSupportStats,
  createSupportConversation,
})(SupportChat);
